/**
 * User Preferences Hook
 * Manages user preferences in localStorage with dextoro: prefix
 * Slippage stored as percentage (2 = 2%)
 */

import { useState, useCallback, useEffect } from 'react';

// LocalStorage keys with prefix
const KEYS = {
  slippage: 'dextoro:slippage',
  priorityFee: 'dextoro:priorityFee',
  currencyDisplay: 'dextoro:currency',
  numberFormat: 'dextoro:numberFormat',
  notificationsEnabled: 'dextoro:notifications',
  soundEnabled: 'dextoro:sound',
  notifyOnTokenTrades: 'dextoro:notifyTokenTrades',
  notifyOnNewFollowers: 'dextoro:notifyNewFollowers',
} as const;

// Default values
const DEFAULTS = {
  slippage: 2, // 2%
  priorityFee: 'fast' as const,
  currencyDisplay: 'usd' as const,
  numberFormat: 'compact' as const,
  notificationsEnabled: true,
  soundEnabled: true,
  notifyOnTokenTrades: true,
  notifyOnNewFollowers: true,
};

export type PriorityFee = 'normal' | 'fast' | 'turbo';
export type CurrencyDisplay = 'usd' | 'sol';
export type NumberFormat = 'compact' | 'full';

export interface UserPreferences {
  defaultSlippage: number;
  priorityFee: PriorityFee;
  currencyDisplay: CurrencyDisplay;
  numberFormat: NumberFormat;
  notificationsEnabled: boolean;
  soundEnabled: boolean;
  notifyOnTokenTrades: boolean;
  notifyOnNewFollowers: boolean;
}

export interface UseUserPreferencesReturn {
  preferences: UserPreferences;
  setSlippage: (value: number) => void;
  setPriorityFee: (value: PriorityFee) => void;
  setCurrencyDisplay: (value: CurrencyDisplay) => void;
  setNumberFormat: (value: NumberFormat) => void;
  setNotificationsEnabled: (value: boolean) => void;
  setSoundEnabled: (value: boolean) => void;
  setNotifyOnTokenTrades: (value: boolean) => void;
  setNotifyOnNewFollowers: (value: boolean) => void;
  clearAll: () => void;
  exportAll: () => Record<string, unknown>;
}

// Safe localStorage access
function getItem<T>(key: string, defaultValue: T): T {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    return JSON.parse(item) as T;
  } catch {
    return defaultValue;
  }
}

function setItem<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error('[useUserPreferences] Failed to save to localStorage:', e);
  }
}

export function useUserPreferences(): UseUserPreferencesReturn {
  const [preferences, setPreferences] = useState<UserPreferences>(() => ({
    defaultSlippage: getItem(KEYS.slippage, DEFAULTS.slippage),
    priorityFee: getItem(KEYS.priorityFee, DEFAULTS.priorityFee),
    currencyDisplay: getItem(KEYS.currencyDisplay, DEFAULTS.currencyDisplay),
    numberFormat: getItem(KEYS.numberFormat, DEFAULTS.numberFormat),
    notificationsEnabled: getItem(KEYS.notificationsEnabled, DEFAULTS.notificationsEnabled),
    soundEnabled: getItem(KEYS.soundEnabled, DEFAULTS.soundEnabled),
    notifyOnTokenTrades: getItem(KEYS.notifyOnTokenTrades, DEFAULTS.notifyOnTokenTrades),
    notifyOnNewFollowers: getItem(KEYS.notifyOnNewFollowers, DEFAULTS.notifyOnNewFollowers),
  }));

  const setSlippage = useCallback((value: number) => {
    const clamped = Math.min(50, Math.max(0.1, value));
    setItem(KEYS.slippage, clamped);
    setPreferences(prev => ({ ...prev, defaultSlippage: clamped }));
  }, []);

  const setPriorityFee = useCallback((value: PriorityFee) => {
    setItem(KEYS.priorityFee, value);
    setPreferences(prev => ({ ...prev, priorityFee: value }));
  }, []);

  const setCurrencyDisplay = useCallback((value: CurrencyDisplay) => {
    setItem(KEYS.currencyDisplay, value);
    setPreferences(prev => ({ ...prev, currencyDisplay: value }));
  }, []);

  const setNumberFormat = useCallback((value: NumberFormat) => {
    setItem(KEYS.numberFormat, value);
    setPreferences(prev => ({ ...prev, numberFormat: value }));
  }, []);

  const setNotificationsEnabled = useCallback((value: boolean) => {
    setItem(KEYS.notificationsEnabled, value);
    setPreferences(prev => ({ ...prev, notificationsEnabled: value }));
  }, []);

  const setSoundEnabled = useCallback((value: boolean) => {
    setItem(KEYS.soundEnabled, value);
    setPreferences(prev => ({ ...prev, soundEnabled: value }));
    // Notify same-tab listeners (StorageEvent only fires cross-tab)
    window.dispatchEvent(new CustomEvent('dextoro:sound-changed', { detail: value }));
  }, []);

  const setNotifyOnTokenTrades = useCallback((value: boolean) => {
    setItem(KEYS.notifyOnTokenTrades, value);
    setPreferences(prev => ({ ...prev, notifyOnTokenTrades: value }));
  }, []);

  const setNotifyOnNewFollowers = useCallback((value: boolean) => {
    setItem(KEYS.notifyOnNewFollowers, value);
    setPreferences(prev => ({ ...prev, notifyOnNewFollowers: value }));
  }, []);

  const clearAll = useCallback(() => {
    Object.values(KEYS).forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.error('[useUserPreferences] Failed to clear key:', key, e);
      }
    });
    setPreferences({
      defaultSlippage: DEFAULTS.slippage,
      priorityFee: DEFAULTS.priorityFee,
      currencyDisplay: DEFAULTS.currencyDisplay,
      numberFormat: DEFAULTS.numberFormat,
      notificationsEnabled: DEFAULTS.notificationsEnabled,
      soundEnabled: DEFAULTS.soundEnabled,
      notifyOnTokenTrades: DEFAULTS.notifyOnTokenTrades,
      notifyOnNewFollowers: DEFAULTS.notifyOnNewFollowers,
    });
  }, []);

  const exportAll = useCallback((): Record<string, unknown> => {
    return {
      preferences: { ...preferences },
      exportedAt: new Date().toISOString(),
    };
  }, [preferences]);

  return {
    preferences,
    setSlippage,
    setPriorityFee,
    setCurrencyDisplay,
    setNumberFormat,
    setNotificationsEnabled,
    setSoundEnabled,
    setNotifyOnTokenTrades,
    setNotifyOnNewFollowers,
    clearAll,
    exportAll,
  };
}
